"use client";

import { type CSSProperties, useMemo, useRef, useEffect } from "react";
import { Box, Group, Stack, Text } from "@mantine/core";
import { trackEvent } from "@/lib/client/analytics";
import styles from "./styles.module.scss";

interface PricePositionBarProps {
  percentile: number;
  comparisonName?: string;
  insight?: { label: string; color: string } | null;
}

const MIN_OFFSET = 4;
const MAX_OFFSET = 96;

export default function PricePositionBar({
  percentile,
  comparisonName = "this brand",
  insight = null,
}: PricePositionBarProps) {
  const clampedPercentile = Math.max(0, Math.min(1, percentile));
  const offsetPercent = Math.max(MIN_OFFSET, Math.min(MAX_OFFSET, clampedPercentile * 100));
  const percentileValue = Math.round(clampedPercentile * 100);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasTracked = useRef(false);

  // Track when the price position bar becomes visible to the user
  useEffect(() => {
    const el = containerRef.current;
    if (!el || hasTracked.current) return;

    try {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && !hasTracked.current) {
            hasTracked.current = true;
            try {
              trackEvent("price_insights_viewed", "engagement", {
                percentile: percentileValue,
                insight_label: insight?.label,
                comparison_name: comparisonName,
              });
            } catch {
              // Analytics must never affect price insights display
            }
            observer.disconnect();
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(el);
      return () => observer.disconnect();
    } catch {
      // IntersectionObserver not supported — skip tracking
    }
  }, [percentileValue, insight?.label, comparisonName]);

  const helperText = useMemo(() => {
    if (insight?.label === "Great deal") {
      return `Priced below most similar ${comparisonName} listings`;
    }
    if (insight?.label === "Priced high" || insight?.label === "Very high") {
      return `Priced above most similar ${comparisonName} listings`;
    }
    return `Priced in line with similar ${comparisonName} listings`;
  }, [comparisonName, insight?.label]);

  const insightLabel = useMemo(() => {
    if (insight?.label === "Great deal") {
      return "Priced below similar listings.";
    }
    if (insight?.label === "Priced high") {
      return "Priced above similar listings.";
    }
    if (insight?.label === "Very high") {
      return "Significantly higher than most similar listings.";
    }
    return "Priced in line with similar listings.";
  }, [insight?.label]);

  const markerCssColor = useMemo(() => {
    const token = insight?.color;
    if (!token) return "#111827";
    if (token === "dimmed") return "var(--mantine-color-dimmed)";
    const parts = token.split(".");
    if (parts.length === 2) {
      const [name, shade] = parts;
      return `var(--mantine-color-${name}-${shade})`;
    }
    return token;
  }, [insight?.color]);

  const ariaLabel = `Price insight: ${helperText}. Position: ${percentileValue}th percentile of ${comparisonName}.`;

  return (
    <Stack gap={1} className={styles.container} ref={containerRef}>
      <Box
        className={styles.barWrapper}
        pos="relative"
        tabIndex={0}
        aria-label={ariaLabel}
        style={
          {
            "--label-fg": markerCssColor,
            "--marker-fg": markerCssColor,
          } as CSSProperties
        }
      >
        <Text className={styles.positionLabel} fw={500}>
          {insightLabel}
        </Text>
        <Box className={styles.bar} />
        <Box className={styles.marker} style={{ left: `${offsetPercent}%` }} />
      </Box>

      <Group justify="space-between">
        <Text size="13px" c="dimmed">
          Below market
        </Text>
        <Text size="13px" c="dimmed">
          Above market
        </Text>
      </Group>
    </Stack>
  );
}
